import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, Calendar, Clock, Folder, Flame, MoreHorizontal, Check, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { pushToast } from '../lib/toast';
import { toasts as copy } from '@shared/copy';
import { relativeDate, isOverdue } from '../lib/dates';

export function TaskRow({
  task,
  onOpen,
  isSelected,
  onSelect,
}: {
  task: any;
  onOpen: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
}) {
  const qc = useQueryClient();
  const isDone = task.status === 'done' || !!task.completed_at;

  const toggle = useMutation({
    mutationFn: async (next: boolean) => (next ? api.completeTask(task.id) : api.uncompleteTask(task.id)),
    onMutate: async (next: boolean) => {
      // Optimistic patch into every cached view list so the checkbox feels instant.
      await qc.cancelQueries({ queryKey: ['view'] });
      await qc.cancelQueries({ queryKey: ['tasks'] });
      const patcher = (list: any) => {
        if (!Array.isArray(list)) return list;
        return list.map((t: any) => (t?.id === task.id ? { ...t, status: next ? 'done' : 'next', completed_at: next ? Date.now() : null } : t));
      };
      qc.setQueriesData({ queryKey: ['view'] }, patcher);
      qc.setQueriesData({ queryKey: ['tasks'] }, patcher);
      return {};
    },
    onSuccess: (_d, next) => {
      pushToast(next ? copy.taskCompleted : 'Back to the pile.', 'success');
      qc.invalidateQueries({ queryKey: ['view'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: () => {
      pushToast('Could not change that task.', 'error');
      qc.invalidateQueries({ queryKey: ['view'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const flag = useMutation({
    mutationFn: () => api.updateTask(task.id, { focus: !task.focus } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['view'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const del = useMutation({
    mutationFn: () => api.deleteTask(task.id),
    onSuccess: () => {
      pushToast(copy.taskDeleted);
      qc.invalidateQueries({ queryKey: ['view'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const overdue = isOverdue(task.due_at);

  // Swipe state (touch only).
  const [dx, setDx] = useState(0);
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const isSwiping = useRef(false);
  const releasing = useRef(false);

  const SWIPE_COMPLETE = 90; // px right to mark complete
  const SWIPE_DELETE = 90; // px left to delete

  function onPointerDown(e: React.PointerEvent) {
    if (e.pointerType !== 'touch') return;
    startX.current = e.clientX;
    startY.current = e.clientY;
    isSwiping.current = false;
    releasing.current = false;
  }
  function onPointerMove(e: React.PointerEvent) {
    if (e.pointerType !== 'touch' || startX.current === null) return;
    const ddx = e.clientX - startX.current;
    const ddy = e.clientY - (startY.current ?? 0);
    if (!isSwiping.current) {
      // Lock direction: only swipe if mostly horizontal
      if (Math.abs(ddx) < 8) return;
      if (Math.abs(ddy) > Math.abs(ddx)) {
        startX.current = null;
        return;
      }
      isSwiping.current = true;
    }
    const clamped = Math.max(-160, Math.min(160, ddx));
    setDx(clamped);
  }
  function onPointerUp(e: React.PointerEvent) {
    if (e.pointerType !== 'touch') return;
    if (startX.current === null) return;
    releasing.current = true;
    const final = dx;
    startX.current = null;
    isSwiping.current = false;
    if (final >= SWIPE_COMPLETE && !isDone) {
      toggle.mutate(true);
    } else if (final <= -SWIPE_DELETE) {
      if (confirm('Delete this task?')) del.mutate();
    }
    setDx(0);
  }
  function onPointerCancel() {
    startX.current = null;
    isSwiping.current = false;
    setDx(0);
  }

  const swipeStyle: React.CSSProperties = {
    transform: `translateX(${dx}px)`,
    transition: releasing.current ? 'transform 180ms ease' : isSwiping.current ? 'none' : 'transform 120ms ease',
    touchAction: 'pan-y',
  };

  return (
    <li
      className={cn(
        'relative overflow-hidden',
        isSelected && 'ring-1 ring-amber-500/60 ring-inset',
      )}
      data-task-id={task.id}
      onMouseEnter={() => onSelect?.()}
    >
      {/* Swipe action backdrops */}
      <div className="absolute inset-y-0 left-0 flex items-center justify-start pl-4 text-emerald-400 pointer-events-none">
        {dx > 12 && (
          <span className={cn('flex items-center gap-1.5 text-sm', dx >= SWIPE_COMPLETE && 'font-medium')}>
            <Check className="w-4 h-4" /> Complete
          </span>
        )}
      </div>
      <div className="absolute inset-y-0 right-0 flex items-center justify-end pr-4 text-red-400 pointer-events-none">
        {dx < -12 && (
          <span className={cn('flex items-center gap-1.5 text-sm', dx <= -SWIPE_DELETE && 'font-medium')}>
            <Trash2 className="w-4 h-4" /> Delete
          </span>
        )}
      </div>

      <div
        style={swipeStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        className={cn(
          'flex items-start gap-3 px-3 py-2.5 group bg-zinc-900/0 hover:bg-zinc-900',
          isDone && 'task-done',
          isSelected && 'bg-zinc-900',
        )}
      >
      <input
        type="checkbox"
        className="doer-check mt-1"
        checked={isDone}
        onChange={(e) => toggle.mutate(e.target.checked)}
        aria-label={isDone ? 'Mark not done' : 'Mark done'}
        onClick={(e) => e.stopPropagation()}
      />
      <button onClick={onOpen} className="flex-1 min-w-0 text-left no-focus-ring">
        <div className="task-title text-sm md:text-[15px] leading-tight">
          {task.title}
        </div>
        {(task.notes || task.due_at || task.defer_at || task.project_id || task.waiting_for) && (
          <div className="mt-1 flex items-center flex-wrap gap-2 text-xs text-zinc-500">
            {task.due_at && (
              <span className={cn('inline-flex items-center gap-1', overdue && 'text-red-400')}>
                <Calendar className="w-3 h-3" />
                {relativeDate(task.due_at)}
              </span>
            )}
            {task.defer_at && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {relativeDate(task.defer_at)}
              </span>
            )}
            {task.waiting_for && (
              <span className="inline-flex items-center gap-1">
                · waiting on {task.waiting_for}
              </span>
            )}
            {task.energy && (
              <span className="inline-flex items-center gap-1 capitalize">
                <Flame className="w-3 h-3" /> {task.energy}
              </span>
            )}
            {task.project_id && (
              <span className="inline-flex items-center gap-1">
                <Folder className="w-3 h-3" />
                project
              </span>
            )}
            {task.notes && <span className="opacity-70 truncate max-w-[40ch]">— {String(task.notes).split('\n')[0]}</span>}
          </div>
        )}
      </button>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => flag.mutate()}
          className={cn(
            'p-2.5 -m-1.5 rounded hover:bg-zinc-800',
            task.focus ? 'text-amber-400' : 'text-zinc-500 md:opacity-60 md:group-hover:opacity-100',
          )}
          aria-label={task.focus ? 'Remove from Today' : 'Flag for Today'}
          aria-pressed={!!task.focus}
          title="Flag for Today"
        >
          <Star className={cn('w-4 h-4', task.focus && 'fill-amber-400')} />
        </button>
        <button
          onClick={onOpen}
          className="p-2.5 -m-1.5 rounded hover:bg-zinc-800 text-zinc-500 md:opacity-0 md:group-hover:opacity-100"
          aria-label="Open task"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>
      </div>
    </li>
  );
}
